/**
 * Portfolio Service
 *
 * Service layer for loading and managing portfolio projects from Markdown files.
 * Uses Vite's import.meta.glob() for file loading and js-yaml for frontmatter parsing.
 */

import yaml from 'js-yaml';
import { marked } from 'marked';
import {
  PortfolioProject,
  PortfolioFilters,
  PortfolioMetadata,
  PortfolioSortOption,
} from '../types/portfolio';

/**
 * Browser-compatible frontmatter parser using js-yaml
 * Parses YAML frontmatter from markdown files
 */
function parseFrontmatter(content: string): { data: Record<string, unknown>; content: string } {
  // Handle different line endings and whitespace
  const normalizedContent = content.replace(/\r\n/g, '\n').trim();
  const frontmatterRegex = /^---\n([\s\S]*?)\n---\n?([\s\S]*)$/;
  const match = normalizedContent.match(frontmatterRegex);

  if (!match) {
    console.warn('[parseFrontmatter] No frontmatter match found');
    return { data: {}, content: normalizedContent };
  }

  const yamlContent = match[1];
  const markdownContent = match[2] || '';

  try {
    const data = yaml.load(yamlContent) as Record<string, unknown>;
    return { data: data || {}, content: markdownContent };
  } catch (error) {
    console.error('[parseFrontmatter] YAML parse error:', error);
    return { data: {}, content: markdownContent };
  }
}

/**
 * Cache for parsed portfolio projects
 * Prevents re-parsing on every request
 */
let projectsCache: PortfolioProject[] | null = null;

// Clear cache on hot module reload during development
if (import.meta.hot) {
  import.meta.hot.accept(() => {
    projectsCache = null;
  });
}

/**
 * Configure marked for safe HTML rendering
 */
marked.setOptions({
  gfm: true, // GitHub Flavored Markdown
  breaks: true, // Convert \n to <br>
});

/**
 * Load all portfolio projects from markdown files
 *
 * Uses Vite's import.meta.glob to dynamically load all .md files
 * from the content/portfolio directory.
 *
 * @returns Promise<PortfolioProject[]> Array of parsed portfolio projects
 */
export async function getPortfolioProjects(): Promise<PortfolioProject[]> {
  // Return cached projects if available
  if (projectsCache) {
    return projectsCache;
  }

  try {
    // Load all markdown files from content/portfolio directory
    const portfolioFiles = import.meta.glob('/content/portfolio/*.md', {
      query: '?raw',
      import: 'default',
      eager: true,
    });

    const projects: PortfolioProject[] = [];

    // Parse each markdown file
    for (const [filepath, content] of Object.entries(portfolioFiles)) {
      try {
        // Parse frontmatter and content using browser-compatible parser
        const { data, content: markdownContent } = parseFrontmatter(content as string);

        // Convert markdown to HTML
        const htmlContent = await marked.parse(markdownContent);

        // Extract slug from filename
        const filename = filepath.split('/').pop() || '';
        const slug = filename.replace('.md', '');

        // Create portfolio project object with proper type casting
        const project: PortfolioProject = {
          title: (data.title as string) || 'Untitled Project',
          client: (data.client as string) || 'Confidential',
          slug: (data.slug as string) || slug,
          isConfidential: (data.isConfidential as boolean) ?? false,
          publishDate: (data.publishDate as string) || new Date().toISOString(),
          featured: (data.featured as boolean) ?? false,
          heroImage: (data.heroImage as string) || '/images/portfolio/placeholder.jpg',
          gallery: (data.gallery as string[]) || [],
          industry: (data.industry as string) || 'Other',
          projectType: (data.projectType as string) || 'Web App',
          techStack: (data.techStack as string[]) || [],
          timeline: (data.timeline as string) || 'N/A',
          teamSize: (data.teamSize as string) || 'N/A',
          role: (data.role as string) || 'Developer',
          challenge: (data.challenge as string) || '',
          solution: (data.solution as string) || '',
          results: (data.results as string[]) || [],
          testimonial: data.testimonial as PortfolioProject['testimonial'],
          content: htmlContent,
          rawContent: markdownContent,
        };

        projects.push(project);
      } catch (error) {
        console.error(`Error parsing portfolio file ${filepath}:`, error);
      }
    }

    // Sort projects by publish date (newest first)
    projects.sort(
      (a, b) => new Date(b.publishDate).getTime() - new Date(a.publishDate).getTime()
    );

    // Cache the results
    projectsCache = projects;

    return projects;
  } catch (error) {
    console.error('Error loading portfolio projects:', error);
    return [];
  }
}

/**
 * Get a single portfolio project by slug
 *
 * @param slug - URL-friendly project identifier
 * @returns Promise<PortfolioProject | null>
 */
export async function getProjectBySlug(slug: string): Promise<PortfolioProject | null> {
  const projects = await getPortfolioProjects();
  return projects.find((project) => project.slug === slug) || null;
}

/**
 * Filter portfolio projects based on criteria
 *
 * Applies AND logic - all active filters must match.
 *
 * @param projects - Array of projects to filter
 * @param filters - Filter criteria
 * @returns Filtered array of projects
 */
export function filterProjects(
  projects: PortfolioProject[],
  filters: PortfolioFilters
): PortfolioProject[] {
  let filtered = [...projects];

  // Filter by industry
  if (filters.industry) {
    filtered = filtered.filter((project) => project.industry === filters.industry);
  }

  // Filter by tech stack (project must have ALL selected technologies)
  if (filters.techStack && filters.techStack.length > 0) {
    filtered = filtered.filter((project) =>
      filters.techStack!.every((tech) => project.techStack.includes(tech))
    );
  }

  // Filter by project type
  if (filters.projectType) {
    filtered = filtered.filter((project) => project.projectType === filters.projectType);
  }

  // Filter by search query (searches title, client, challenge, solution)
  if (filters.searchQuery) {
    const query = filters.searchQuery.toLowerCase();
    filtered = filtered.filter(
      (project) =>
        project.title.toLowerCase().includes(query) ||
        project.client.toLowerCase().includes(query) ||
        project.challenge.toLowerCase().includes(query) ||
        project.solution.toLowerCase().includes(query)
    );
  }

  return filtered;
}

/**
 * Sort portfolio projects
 *
 * @param projects - Array of projects to sort
 * @param sortOption - Sort criteria
 * @returns Sorted array of projects
 */
export function sortProjects(
  projects: PortfolioProject[],
  sortOption: PortfolioSortOption = 'date-desc'
): PortfolioProject[] {
  const sorted = [...projects];

  switch (sortOption) {
    case 'date-desc':
      return sorted.sort(
        (a, b) => new Date(b.publishDate).getTime() - new Date(a.publishDate).getTime()
      );
    case 'date-asc':
      return sorted.sort(
        (a, b) => new Date(a.publishDate).getTime() - new Date(b.publishDate).getTime()
      );
    case 'title-asc':
      return sorted.sort((a, b) => a.title.localeCompare(b.title));
    case 'title-desc':
      return sorted.sort((a, b) => b.title.localeCompare(a.title));
    default:
      return sorted;
  }
}

/**
 * Get featured portfolio projects
 *
 * @param projects - Array of all projects
 * @param limit - Maximum number of featured projects to return
 * @returns Array of featured projects
 */
export function getFeaturedProjects(
  projects: PortfolioProject[],
  limit: number = 3
): PortfolioProject[] {
  return projects.filter((project) => project.featured).slice(0, limit);
}

/**
 * Get unique industries from all projects
 *
 * @param projects - Array of all projects
 * @returns Sorted array of unique industries
 */
export function getUniqueIndustries(projects: PortfolioProject[]): string[] {
  const industries = new Set(projects.map((project) => project.industry));
  return Array.from(industries).sort();
}

/**
 * Get unique technologies from all projects
 *
 * @param projects - Array of all projects
 * @returns Sorted array of unique technologies
 */
export function getUniqueTechStack(projects: PortfolioProject[]): string[] {
  const techStack = new Set(projects.flatMap((project) => project.techStack));
  return Array.from(techStack).sort();
}

/**
 * Get unique project types from all projects
 *
 * @param projects - Array of all projects
 * @returns Sorted array of unique project types
 */
export function getUniqueProjectTypes(projects: PortfolioProject[]): string[] {
  const projectTypes = new Set(projects.map((project) => project.projectType));
  return Array.from(projectTypes).sort();
}

/**
 * Get portfolio metadata
 *
 * Aggregates metadata about all portfolio projects for use in filters
 * and overview displays.
 *
 * @returns Promise<PortfolioMetadata>
 */
export async function getPortfolioMetadata(): Promise<PortfolioMetadata> {
  const projects = await getPortfolioProjects();

  return {
    totalProjects: projects.length,
    industries: getUniqueIndustries(projects),
    techStack: getUniqueTechStack(projects),
    projectTypes: getUniqueProjectTypes(projects),
    featuredProjects: getFeaturedProjects(projects),
  };
}

/**
 * Clear the projects cache
 * Useful for development when markdown files are updated
 */
export function clearCache(): void {
  projectsCache = null;
}

/**
 * Get paginated projects
 *
 * @param projects - Array of projects to paginate
 * @param page - Current page (1-indexed)
 * @param perPage - Items per page
 * @returns Object with paginated projects and pagination info
 */
export function paginateProjects(
  projects: PortfolioProject[],
  page: number = 1,
  perPage: number = 9
): {
  projects: PortfolioProject[];
  pagination: {
    page: number;
    perPage: number;
    total: number;
    totalPages: number;
    hasNext: boolean;
    hasPrev: boolean;
  };
} {
  const total = projects.length;
  const totalPages = Math.ceil(total / perPage);
  const start = (page - 1) * perPage;
  const end = start + perPage;

  return {
    projects: projects.slice(start, end),
    pagination: {
      page,
      perPage,
      total,
      totalPages,
      hasNext: page < totalPages,
      hasPrev: page > 1,
    },
  };
}
